import React, { useState, useEffect, useMemo } from "react";
import { X, CheckCircle2, XCircle, Calendar } from "lucide-react";
import Loader from "../../Shared/Loader";
import { showToast } from "../../Shared/Toaster";
import api from "../../../api/axiosConfig";
import "../../../css/MoveAppointmentsModal.css";

interface Appointment {
  id: string;
  appointmentId: string;
  centerId: string;
  centerName: string;
  centerType: string;
  city: string;
  startDate: string;
  endDate: string;
  slotDesigns: Record<string, string>;
  totalSlots: number;
  appointmentsPerSlot: number;
  totalAppointments: number;
}

interface TargetCluster {
  clusterId: string;
  centerName: string;
  clusterStartDate: string;
  clusterEndDate: string;
  hasConflict: boolean;
  required: number;
  available: number;
  available_date_ranges?: {
    startDate: string;
    endDate: string;
    dates: string[];
  }[];
}

interface MoveAppointmentsModalProps {
  sourceAppointment: Appointment;
  onClose: () => void;
  onSuccess: () => void;
}

const MoveAppointmentsModal: React.FC<MoveAppointmentsModalProps> = ({
  sourceAppointment,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Process Date in Source Configuration (defaults to source start date)
  const [processDate, setProcessDate] = useState<string>(
    sourceAppointment.startDate || ""
  );

  // Targets list from check-transfer-status
  const [targets, setTargets] = useState<TargetCluster[]>([]);
  // Target selected dates: targetClusterId -> chosen target date
  const [selectedTargetDates, setSelectedTargetDates] = useState<
    Record<string, string>
  >({});

  // Filter by Date for the targeted clusters table
  const [filterDate, setFilterDate] = useState<string>("");

  // Transferring state (for loading spinner on Move button)
  const [transferringClusterId, setTransferringClusterId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchClustersAndStatus = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("accessToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1. Fetch All Clusters
        const clusterRes = await api.appointment_api.get(
          "/api/cluster/clusters",
          { headers }
        );
        const allClusters: any[] =
          clusterRes.data?.items || clusterRes.data || [];

        // Filter out source cluster
        const sourceId =
          sourceAppointment.appointmentId || sourceAppointment.id;
        const otherClusters = allClusters.filter(
          (c) =>
            c.appointment_cluster_id !== sourceId &&
            c._id !== sourceId &&
            c.appointment_cluster_id !== sourceAppointment.id &&
            c._id !== sourceAppointment.appointmentId
        );

        const targetClusterIds = otherClusters.map(
          (c) => c.appointment_cluster_id || c._id
        );

        if (targetClusterIds.length === 0) {
          setTargets([]);
          setLoading(false);
          return;
        }

        // 2. Check Transfer Status
        const statusRes = await api.appointment_api.post(
          "/api/cluster/clusters/check-transfer-status",
          {
            sourceClusterId: sourceId,
            targetClusterIds,
          },
          { headers }
        );

        const targetList: TargetCluster[] = statusRes.data?.targets || [];
        setTargets(targetList);

        // Initialize each target's selected date
        const initialDates: Record<string, string> = {};
        targetList.forEach((t) => {
          const defaultDate =
            t.available_date_ranges?.[0]?.startDate ||
            t.clusterStartDate ||
            "";
          initialDates[t.clusterId] = defaultDate;
        });
        setSelectedTargetDates(initialDates);
      } catch (err: any) {
        console.error("Failed to check transfer status:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load target clusters. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchClustersAndStatus();
  }, [sourceAppointment]);

  // Filtered targets based on filterDate
  const filteredTargets = useMemo(() => {
    if (!filterDate) return targets;

    return targets.filter((target) => {
      // Check if filterDate is within available ranges or cluster start/end
      if (
        target.available_date_ranges &&
        target.available_date_ranges.length > 0
      ) {
        return target.available_date_ranges.some((range) => {
          if (range.dates && range.dates.length > 0) {
            return range.dates.includes(filterDate);
          }
          return filterDate >= range.startDate && filterDate <= range.endDate;
        });
      }

      return (
        filterDate >= target.clusterStartDate &&
        filterDate <= target.clusterEndDate
      );
    });
  }, [targets, filterDate]);

  const handleDateChangeForTarget = (clusterId: string, dateVal: string) => {
    setSelectedTargetDates((prev) => ({
      ...prev,
      [clusterId]: dateVal,
    }));
  };

  const handleMove = async (target: TargetCluster) => {
    const targetStartDate = selectedTargetDates[target.clusterId];

    if (!targetStartDate) {
      showToast("Please select a target date.", "error");
      return;
    }

    if (!processDate) {
      showToast("Please select a valid Process Date.", "error");
      return;
    }

    try {
      setTransferringClusterId(target.clusterId);
      const token = sessionStorage.getItem("accessToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const sourceId =
        sourceAppointment.appointmentId || sourceAppointment.id;

      const payload = {
        sourceClusterId: sourceId,
        targetClusterId: target.clusterId,
        startDate: processDate,
        endDate: processDate,
        targetStartDate,
        targetEndDate: targetStartDate,
      };

      const res = await api.appointment_api.post(
        "/api/cluster/clusters/transfer-bookings",
        payload,
        { headers }
      );

      const count = res.data?.transferred_count ?? 1;
      showToast(
        res.data?.message ||
          `Transfer successful! ${count} booking(s) transferred.`,
        "success"
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Transfer bookings failed:", err);
      showToast(
        err.response?.data?.message || "Failed to transfer bookings.",
        "error"
      );
    } finally {
      setTransferringClusterId(null);
    }
  };

  return (
    <div className="move-modal-overlay" onClick={onClose}>
      <div
        className="move-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="move-modal-header">
          <h2 className="move-modal-title">Move Data - Task Configuration</h2>
          <button className="move-modal-close-btn" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="move-modal-body">
          {/* Source Configuration Section */}
          <div className="move-section">
            <h3 className="move-section-title">Source Configuration</h3>
            <div className="move-source-grid">
              <div className="move-form-group">
                <label className="move-label">Selected Source Cluster</label>
                <input
                  type="text"
                  className="move-input-readonly"
                  value={sourceAppointment.centerName}
                  readOnly
                  disabled
                />
              </div>

              <div className="move-form-group">
                <label className="move-label">Process Date</label>
                <div className="move-date-input-wrapper">
                  <input
                    type="date"
                    className="move-date-input"
                    value={processDate}
                    min={sourceAppointment.startDate}
                    max={sourceAppointment.endDate}
                    onChange={(e) => setProcessDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Targeted Clusters Section */}
          <div className="move-section">
            <div className="move-targeted-header">
              <h3 className="move-section-title">Targeted Clusters</h3>
              <div className="move-filter-date-wrapper">
                <div className="move-filter-date-box">
                  <Calendar size={16} className="move-filter-icon" />
                  <input
                    type="date"
                    className="move-filter-date-input"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    title="Filter by Date"
                  />
                  {filterDate && (
                    <button
                      type="button"
                      className="move-filter-clear-btn"
                      onClick={() => setFilterDate("")}
                      title="Clear filter"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="move-loader-wrap">
                <Loader text="Loading target cluster configurations..." />
              </div>
            ) : error ? (
              <div className="move-error-box">{error}</div>
            ) : (
              <div className="move-table-wrapper">
                <table className="move-clusters-table">
                  <thead>
                    <tr>
                      <th>Cluster</th>
                      <th>Date</th>
                      <th>Available</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTargets.length > 0 ? (
                      filteredTargets.map((target) => {
                        const isAvailable =
                          !target.hasConflict && target.available > 0;

                        // Allowed date boundaries
                        const minDate =
                          target.available_date_ranges?.[0]?.startDate ||
                          target.clusterStartDate;
                        const maxDate =
                          target.available_date_ranges?.[0]?.endDate ||
                          target.clusterEndDate;

                        const rowDate =
                          selectedTargetDates[target.clusterId] || "";

                        return (
                          <tr key={target.clusterId}>
                            <td className="move-cluster-name">
                              {target.centerName || target.clusterId}
                            </td>
                            <td>
                              <div className="move-table-date-wrap">
                                <input
                                  type="date"
                                  className="move-table-date-input"
                                  value={rowDate}
                                  min={minDate}
                                  max={maxDate}
                                  onChange={(e) =>
                                    handleDateChangeForTarget(
                                      target.clusterId,
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              {isAvailable ? (
                                <span className="move-status-pill available">
                                  <CheckCircle2 size={16} /> Yes
                                </span>
                              ) : (
                                <span className="move-status-pill unavailable">
                                  <XCircle size={16} /> No
                                </span>
                              )}
                            </td>
                            <td>
                              {isAvailable ? (
                                <button
                                  type="button"
                                  className="btn-move-submit"
                                  onClick={() => handleMove(target)}
                                  disabled={
                                    transferringClusterId === target.clusterId
                                  }
                                >
                                  {transferringClusterId === target.clusterId
                                    ? "Moving..."
                                    : "Move"}
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            textAlign: "center",
                            padding: "24px",
                            color: "#64748b",
                          }}
                        >
                          No targeted clusters available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="move-modal-footer">
          <button type="button" className="btn-move-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveAppointmentsModal;
